<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div class="workflow-wrap">
      <div class="workflow-header" v-if="areFiltersVisible">
        <div class="range-selector agenda-range" v-if="isDateFilterVisible">
          <div class="range-column day">
            <div class="form-group">
              <div style="display: flex">
                <button type="button" class="btn btn-default btn-xs" v-on:click="moveBack"><i class="fa fa-arrow-left"></i></button>
                <DatePicker v-model="dateStart" v-on:change="inputDateStart" :shortLocale="shortLocale" :staticInput="true" :placeholder="_('Select date')"></DatePicker>
                <button type="button" class="btn btn-default btn-xs" v-on:click="moveForward"><i class="fa fa-arrow-right"></i></button>
                <button class="btn btn-default" type="button" v-on:click="setToday">{{ _('Today') }}</button>
              </div>
            </div>
          </div>
          <div class="range-column duration">
            <div class="form-group">
              <select class="form-control" v-model="duration">
                <option value="">{{ _('Show all') }}</option>
                <option value="1d">{{ _('One day') }}</option>
                <option value="2d">{{ _('Two days') }}</option>
                <option value="3d">{{ _('Three days') }}</option>
                <option value="1w">{{ _('One week') }}</option>
                <option value="2w">{{ _('Two weeks') }}</option>
                <option value="1M">{{ _('One month') }}</option>
              </select>
            </div>
          </div>
        </div>
        <div v-if="isSearchVisible">
          <div class="form-group">
            <input class="form-control" id="searchField" type="text" v-model="search" :placeholder="_('Keyword search')" />
          </div>
        </div>
      </div>
      <div class="workflow-row" style="height: 0px">
        <div class="workflow-column" v-for="(column, index) in enabledColumns">
          <h4 class="flex-row space-between"><span>{{ column.title }}</span><button class="workflow-info btn btn-default btn-xs" v-on:click="showColumnSummary(index)"><i class="fa fa-fw fa-info"></i></button></h4>
          <div class="list-group">
            <draggable group="flows" v-model="sortedWorkflowItems[index]" draggable=".workflow-item-wrap" :sort="false" :delay="100" :delayOnTouchOnly="true" @change="listChanged(index, $event)" @end="saveTags">
              <WorkflowItem v-for="workflowItem in sortedWorkflowItems[index]" :key="workflowItem.id" :item="workflowItem" :mode="mode" :settings="settings" v-on:itemDetails="showItemDetails(workflowItem)"></WorkflowItem>
            </draggable>
          </div>
        </div>
      </div>
      <WorkflowOrderDetails ref="orderDetails"
                     v-if="selectedOrder && isOrderVisible"
                     :column="column"
                     :workflow-columns="enabledColumns"
                     :tags="columnTags"
                     :order="selectedOrder"
                     v-on:changeTag="changeTag"
                     v-on:setProperty="setOrderProperty"
                     v-on:saveNotes="saveNotes"
                     v-on:closeModal="closeOrderDetails"
                     v-on:saveChanges="saveOrderDetails"
                     v-on:workflowColumnClicked="setWorkflowColumn"
                     v-on:displayOrder="displayOrder"
                     v-on:modifyOrder="modifyOrder"
        ></WorkflowOrderDetails>
      <delivery-details ref="deliveryDetails"
                        v-if="selectedDelivery && isDeliveryVisible"
                        :column="column"
                        :workflow-columns="enabledColumns"
                        :tags="columnTags"
                        :baseDomain="baseDomain"
                        :shopId="shopId"
                        :delivery="selectedDelivery"
                        v-on:changeTag="changeTag"
                        v-on:setProperty="setDeliveryProperty"
                        v-on:saveNotes="saveNotes"
                        v-on:closeModal="closeDeliveryDetails"
                        v-on:saveChanges="saveDeliveryDetails"
                        v-on:workflowColumnClicked="setWorkflowColumn"
                        v-on:displayOrder="displayOrder"
                        v-on:modifyOrder="modifyOrder"
        ></delivery-details>
      <column-summary ref="columnSummary" v-if="isColumnSummaryVisible" :summary="summary" :baseDomain="baseDomain" :shopId="shopId" v-on:closeModal="closeColumnSummary"></column-summary>
    </div>
  </div>
</template>

<script>
  
import axios from 'axios'
import components from '~/lib/components'
import settingshelpers from '~/lib/settingshelpers'
import { WorkflowItem, WorkflowOrderDetails, WorkflowDeliveryDetails as DeliveryDetails, WorkflowColumnSummary as ColumnSummary } from '#components'
import { DateTime } from 'luxon'
import debounce from 'lodash/debounce'

export default {
  data () {
    return {
      tags: [],
      selectedOrder: null,
      selectedDelivery: null,
      isOrderVisible: false,
      isDeliveryVisible: false,
      workflowItems: {},
      activeWorkflowColumn: null,
      itemId: null,
      isColumnSummaryVisible: false,
      orders: {},
      orderId: null,
      addTag: '',
      removeTag: '',
      setStatus: null,
      maxItems: 100,
      range: {},
      duration: '1d',
      search: '',
      summary: {},
      nowDuration: 1 // todo make this configurable
    }
  },
  mixins: [components, settingshelpers],
  computed: {
    data () {
      return (this.component.data !== '' ? JSON.parse(this.component.data) : false)
    },
    mode () {
      return this.data && this.data.mode === 'deliveries' ? 'deliveries' : 'orders'
    },
    defaultDuration () {
      return this.data && this.data.defaultDuration ? this.data.defaultDuration : 'all'
    },
    areFiltersVisible () {
      return this.isDateFilterVisible || this.isSearchVisible
    },
    isDateFilterVisible () {
      return this.data && parseInt(this.data.showFilters)
    },
    isSearchVisible () {
      return this.data && parseInt(this.data.showSearch)
    },
    columnWidth () {
      return Math.round(12 / this.enabledColumnCount)
    },
    enabledColumnCount () {
      var c = 0
      if (!this.data || !this.data.columns) {
        return c + 1
      }
      for (var col in this.enabledColumns) {
        if (Object.prototype.hasOwnProperty.call(this.data.columns, col)) {
          c++
        }
      }
      return c
    },
    enabledColumns () {
      let ret = {}
      for (var col in this.data.columns) {
        if (Object.prototype.hasOwnProperty.call(this.data.columns, col)) {
          if (this.data.columns[col].title) {
            ret[col] = this.data.columns[col]
          }
        }
      }
      return ret
    },
    columnTags () {
      let ret = {}
      if (!this.data || !this.data.columns) {
        return ret
      }
      for (var col in this.data.columns) {
        if (Object.prototype.hasOwnProperty.call(this.data.columns, col)) {
          if (this.data.columns[col].filter) {
            ret[col] = this.getTagById(this.data.columns[col].filter)
          }
        }
      }
      return ret
    },
    columnStatus () {
      let ret = {}
      if (!this.data || !this.data.columns) {
        return ret
      }
      for (var col in this.data.columns) {
        if (Object.prototype.hasOwnProperty.call(this.data.columns, col)) {
          if (this.data.columns[col].status) {
            ret[col] = this.data.columns[col].status
          }
        }
      }
      return ret
    },
    columnFilters () {
      let ret = []
      if (!this.data || !this.data.columns) {
        return ret
      }
      for (var col in this.data.columns) {
        if (Object.prototype.hasOwnProperty.call(this.data.columns, col)) {
          if (this.data.columns[col].filter) {
            ret.push(this.data.columns[col].filter)
          }
        }
      }
      return ret
    },
    dateStart: {
      get() {
        if (!this.range || !this.range.datestart) {
          return null
        }
        return DateTime.fromFormat(this.range.datestart, 'd.M.yyyy').toJSDate()
      },
      set(newValue) {
        if (newValue) {
          this.range.datestart = DateTime.fromJSDate(newValue).toFormat('d.M.yyyy')
        } else {
          this.range.datestart = null
        }
      }
    },
    start () {
      if (!this.dateStart) {
        return null
      }
      return DateTime.fromJSDate(this.dateStart).toFormat('yyyy-MM-dd')
    },
    end () {
      if (!this.dateStart || !this.duration) {
        return null
      }
      const len = this.duration.charAt(0)
      let dur = this.duration.charAt(1)
      switch (dur) {
        case 'd':
          dur = 'days'
          break
        case 'w':
          dur = 'weeks'
          break
        case 'M':
          dur = 'months'
          break
      }
      return DateTime.fromJSDate(this.dateStart).plus({ [dur]: Number(len) }).toFormat('yyyy-MM-dd')
    },
    sortedWorkflowItems () {
      if (!this.workflowItems) {
        return {}
      }
      let ret = {}
      for (let col in this.workflowItems) {
        if (Object.prototype.hasOwnProperty.call(this.workflowItems, col)) {
          ret[col] = this.workflowItems[col].filter(c => parseInt(c.pinned)).concat(this.workflowItems[col].filter(c => !parseInt(c.pinned))).map(i => ({ ...i, activeWorkflowColumn: col }))
        }
      }
      return ret
    }
  },
  methods: {
    moveBack () {
      this.range.datestart = DateTime.fromFormat(this.start, 'yyyy-MM-dd', { zone: this.timezone }).minus({ days: 1 }).toFormat(this.dateFormat)
    },
    moveForward () {
      this.range.datestart = DateTime.fromFormat(this.start, 'yyyy-MM-dd', { zone: this.timezone }).plus({ days: 1 }).toFormat(this.dateFormat)
    },
    setToday () {
      this.range.datestart = DateTime.now({ zone: this.timezone }).toFormat(this.dateFormat)
    },
    inputDateStart(newValue) {
      this.input('datestart', newValue)
    },
    input (prop, newValue) {
      if (!(newValue instanceof Date)) {
        return
      }
      this.range[prop] = DateTime.fromJSDate(newValue, { zone: this.timezone }).toFormat(this.dateFormat)
    },
    showColumnSummary (columnIndex) {
      const column = this.enabledColumns[columnIndex]
      const products = {}
      const orders = {}
      const pinned = {}
      const deliveries = {}
      this.sortedWorkflowItems[columnIndex].forEach(flowItem => {
        orders[flowItem.order_id] = true
        if (flowItem.order && parseInt(flowItem.order.pinned)) {
          pinned[flowItem.order_id] = true
        }
        flowItem.ownItem.forEach(item => {
          if (item.product_id) {
            if (!products[item.product_id]) {
              products[item.product_id] = {
                name: item.product_name,
                count: item.count ? parseFloat(item.count) : 0,
                reservation_count: item.reservation_count ? parseInt(item.reservation_count, 10) : 0,
                resource_count: item.resource_count ? parseInt(item.resource_count, 10) : 0
              }
            } else {
              if (item.count) {
                products[item.product_id].count += parseFloat(item.count)
              }
              if (item.reservation_count) {
                products[item.product_id].reservation_count += parseInt(item.reservation_count, 10)
              }
              if (item.resource_count) {
                products[item.product_id].resource_count += parseInt(item.resource_count, 10)
              }
            }
          }
        })
      })
      if (this.mode === 'orders') {
        this.summary = {
          title: column.title,
          products,
          mode: 'orders',
          orders: {
            total: this.sortedWorkflowItems[columnIndex].length,
            pinned: this.sortedWorkflowItems[columnIndex].filter(i => parseInt(i.pinned)).length,
            late: this.sortedWorkflowItems[columnIndex].filter(i => i.delivery_ts < DateTime.now().toUnixInteger()).length,
            now: this.sortedWorkflowItems[columnIndex].filter(i => i.delivery_ts >= DateTime.now().toUnixInteger() && i.delivery_ts < DateTime.now().plus({ hours: this.nowDuration }).toUnixInteger()).length,
          }
        }
      } else {
        this.summary = {
          title: column.title,
          products,
          mode: 'deliveries',
          orders: {
            total: Object.keys(orders).length
          },
          deliveries: {
            total: this.sortedWorkflowItems[columnIndex].length,
            pinned: this.sortedWorkflowItems[columnIndex].filter(i => parseInt(i.pinned)).length,
            late: this.sortedWorkflowItems[columnIndex].filter(i => i.ts < DateTime.now().toUnixInteger()).length,
            now: this.sortedWorkflowItems[columnIndex].filter(i => i.ts >= DateTime.now().toUnixInteger() && i.ts < DateTime.now().plus({ hours: this.nowDuration }).toUnixInteger()).length,
          }
        }
      }
      this.isColumnSummaryVisible = true
    },
    closeColumnSummary () {
      this.isColumnSummaryVisible = false
      this.summary = {}
    },
    showItemDetails (item) {
      this.activeWorkflowColumn = item.activeWorkflowColumn
      if (this.mode === 'orders') {
        return this.showOrderDetails(item)
      } else {
        return this.showDeliveryDetails(item)
      }
    },
    async showDeliveryDetails (item) {
      if (!item) {
        return
      }
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/deliveries/' + item.id + '.json?deliverymethods=true&orders=true&tags=true&items=true&reservations=true&apikey=' + this.apiKey)
      } catch(e) {
        this.closeDeliveryDetails()
        return
      }
      this.selectedDelivery = response.data
      this.selectedDelivery.activeWorkflowColumn = item.activeWorkflowColumn
      this.selectedDelivery.productNames = item.productNames ? item.productNames.toString().split(';') : []
      this.selectedDelivery.resourceNames = item.resourceNames ? item.resourceNames.toString().split(';') : []
      this.isDeliveryVisible = true
    },
    closeDeliveryDetails () {
      this.isDeliveryVisible = false
      this.selectedDelivery = null
      this.activeWorkflowColumn = null
    },
    saveDeliveryDetails () {
      this.closeDeliveryDetails()
    },
    async showOrderDetails (item) {
      if (!item) {
        return
      }
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/orders/' + item.id + '.json?deliveries=true&items=true&reservations=true&apikey=' + this.apiKey)
      } catch(e) {
        this.closeOrderDetails()
        return
      }
      this.selectedOrder = response.data
      this.selectedOrder.activeWorkflowColumn = item.activeWorkflowColumn
      this.isOrderVisible = true
    },
    closeOrderDetails () {
      this.isOrderVisible = false
      this.selectedOrder = null
      this.activeWorkflowColumn = null
    },
    saveOrderDetails () {
      this.closeOrderDetails()
    },
    listChanged (index, evt) {
      if (evt.added) {
        this.itemId = evt.added.element.id
        if (this.columnTags[index]) {
          this.addTag = this.columnTags[index].value
        } else {
          this.addTag = ''
        }
        switch(this.columnStatus[index]) {
          case 'notprocessed':
            this.setStatus = { processed: 0 }
            break
          case 'processed':
            this.setStatus = { processed: 1 }
            break
          default:
            this.setStatus = null
            break
        }
      }
      if (evt.removed) {
        this.itemId = evt.removed.element.id
        if (this.columnTags[index]) {
          this.removeTag = this.columnTags[index].value
        } else {
          this.removeTag = ''
        }
      }
    },
    async saveTags () {
      if (this.itemId) {
        let data = {
          id: this.itemId,
          addtags: this.addTag,
          removetags: this.removeTag
        }
        if (this.setStatus) {
          data.processed = this.setStatus.processed
        }
        await axios.patch(this.baseDomain + this.shopId + '/' + this.mode + '/' + this.itemId + '.json?apikey=' + this.apiKey, data)
      }
      this.itemId = false
      this.addTag = this.removeTag = ''
    },
    async changeTag(tagData) {
      if (this.selectedOrder) {
        this.itemId = this.selectedOrder.id
      } else {
        this.itemId = this.selectedDelivery.id
      }
      this.addTag = ''
      this.removeTag = ''
      if (tagData.old) {
        this.removeTag = tagData.old.value
      }
      if (tagData.new) {
        this.addTag = tagData.new.value
      }
      await this.saveTags()
      this.showItemDetails(this.selectedOrder ? this.selectedOrder : this.selectedDelivery)
    },
    async setOrderProperty(data) {
      data.id = this.selectedOrder.id
      await axios.put(this.baseDomain + this.shopId + '/orders/' + this.selectedOrder.id + '.json?apikey=' + this.apiKey, data)
      this.showItemDetails(this.selectedOrder)
    },
    async setDeliveryProperty(data) {
      data.id = this.selectedDelivery.id
      await axios.put(this.baseDomain + this.shopId + '/deliveries/' + this.selectedDelivery.id + '.json?apikey=' + this.apiKey, data)
      this.showItemDetails(this.selectedDelivery)
    },
    async setWorkflowColumn(index) {
      this.itemId = (this.selectedOrder ? this.selectedOrder.id : this.selectedDelivery.id)
      this.addTag = this.removeTag = ''
      if (this.columnTags[index]) {
        this.addTag = this.columnTags[index].value
      }
      switch(this.columnStatus[index]) {
        case 'notprocessed':
          this.setStatus = { processed: 0 }
          break
        case 'processed':
          this.setStatus = { processed: 1 }
          break
        default:
          this.setStatus = null
          break
      }
      if (this.columnTags[this.activeWorkflowColumn]) {
        this.removeTag = this.columnTags[this.activeWorkflowColumn].value
      }
      await this.saveTags()
      if (this.selectedOrder) {
        this.selectedOrder.activeWorkflowColumn = index
        this.showItemDetails(this.selectedOrder)
      } else {
        this.selectedDelivery.activeWorkflowColumn = index
        this.showItemDetails(this.selectedDelivery)
      }
    },
    async saveNotes(notes) {
      const orderId = this.selectedOrder ? this.selectedOrder.id : this.selectedDelivery.order.id
      let data = {
        id: orderId,
        notes: notes
      }
      await axios.put(this.baseDomain + this.shopId + '/orders/' + orderId + '.json?apikey=' + this.apiKey, data)
      this.showItemDetails(this.selectedOrder ? this.selectedOrder : this.selectedDelivery)
    },
    displayOrder (order, skipState) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'OrderDetails',
        title: this._('Order') + ': ' + order.id,
        width: 6,
        data: {
          orderId: order.id
        },
        skipState
      })
      this.closeOrderDetails()
      this.closeDeliveryDetails()
    },
    modifyOrder (order) {
      this.EventBus.$emit('modifyOrder', {
        order
      })
      this.closeOrderDetails()
      this.closeDeliveryDetails()
    },
    getTagById (id) {
      if (!this.tags || !this.tags.length) {
        return false
      }
      for (let i = 0; i < this.tags.length; i++) {
        if (this.tags[i].id == id) {
          return this.tags[i]
        }
      }
      return false
    },
    shopSettings (shopId) {
      return this.allSettings[shopId + '-' + this.locale] || {}
    },
    setActiveComponent (component) {
      this.$emit('setActiveComponent', component)
    },
    loadTags () {
      const type = this.mode === 'deliveries' ? 'delivery' : 'order'
      axios.get(this.baseDomain + this.shopId + '/tags.json?type=' + type + '&apikey=' + this.apiKey)
      .then(({ data }) => {
        this.tags = data
      })
    },
    loadColumns () {
      let $this = this
      let urlArray = []
      let columnIds = []
      for (var col in this.enabledColumns) {
        let ignoreStr = ''
        if (Object.prototype.hasOwnProperty.call(this.data.columns, col)) {
          let column = this.data.columns[col]
          let tagArr = []
          if (this.data.filter) {
            tagArr.push(this.data.filter)
          }
          if (column.filter) {
            tagArr.push(column.filter)
          } else {
            tagArr = tagArr.concat(this.columnFilters)
            ignoreStr = this.mode === 'orders' ? '&ignoreordertags=true' : '&ignoredeliverytags=true'
          }
          let rangeStr = ''
          if (this.start && this.end && this.isDateFilterVisible) {
            rangeStr = '&delivery_start=' + this.start + '&delivery_end=' + this.end
          }
          let searchStr = ''
          if (this.search && this.isSearchVisible) {
            searchStr = '&sSearch=' + escape(this.search)
          }
          let tagStr = tagArr.join(',')
          let statusStr = ''
          if (this.mode === 'orders') {
            if (column.status) {
              switch (column.status) {
                case 'open':
                  statusStr = '&sSearch_10=open'
                  break
                case 'confirmed':
                  statusStr = '&sSearch_15=1'
                  break
                case 'notpaid':
                  statusStr = '&sSearch_4=0'
                  break
                case 'paid':
                  statusStr = '&sSearch_4=1'
                  break
                case 'refunded':
                  statusStr = '&sSearch_8=1'
                  break
                case 'notprocessed':
                  statusStr = '&sSearch_6=0'
                  break
                case 'processed':
                  statusStr = '&sSearch_6=1'
                  break
                case 'paidnotprocessed':
                  statusStr = '&sSearch_6=0&sSearch_4=1'
                  break
              }
            }
            let dateSortCol = 19 // delivery_ts
            let dateSortDir = 'asc'
            if (!this.isFeatureEnabled('delivery2.0')) {
              dateSortCol = 7 // orderdate
              dateSortDir = 'desc'
            }
            urlArray.push(axios.get(this.baseDomain + this.shopId + '/orders.json?workflow=true&items=true&seats=true&sSearch_11=' + tagStr + searchStr + rangeStr + statusStr + ignoreStr + '&iSortCol_0=18&sSortDir_0=desc&iSortCol_1=' + dateSortCol + '&sSortDir_1=' + dateSortDir + '&iSortCol_2=0&sSortDir_2=asc&iSortingCols=3&iDisplayStart=0&iDisplayLength=' + this.maxItems + '&apikey=' + this.apiKey))
          } else {
            if (column.status) {
              switch (column.status) {
                case 'notprocessed':
                  statusStr = '&sSearch_4=0'
                  break
                case 'processed':
                  statusStr = '&sSearch_4=1'
                  break
              }
            }
            urlArray.push(axios.get(this.baseDomain + this.shopId + '/deliveries.json?workflow=true&orders=true&items=true&seats=true&sSearch_5=' + tagStr + searchStr + rangeStr + statusStr + ignoreStr + '&iSortCol_0=0&sSortDir_0=asc&iSortingCols=1&iDisplayStart=0&iDisplayLength=' + this.maxItems + '&apikey=' + this.apiKey))
          }
          columnIds.push(col)
        }
      }
      axios.all(urlArray)
      .then(axios.spread((...args) => {
        let ret = []
        let tsField = $this.mode === 'orders' ? 'delivery_ts' : 'ts'
        for (let i = 0; i < args.length; i++) {
          let prevSeparator = null
          for (let j = 0; j < args[i].data.length; j++) {
            const item = args[i].data[j]
            const ts = item[tsField]
            if (parseInt(item.pinned)) {
              continue
            }
            if (!ts && prevSeparator !== 'normal') {
              prevSeparator = 'normal'
              if (j > 0) {
                item.separator = prevSeparator
              }
            }
            if (ts && ts < DateTime.now().toUnixInteger() && prevSeparator !== 'late') {
              prevSeparator = 'late'
              if (j > 0) {
                item.separator = prevSeparator
              }
              item.late = true
            }
            if (ts && ts > DateTime.now().toUnixInteger() && ts < DateTime.now().plus({ hours: $this.nowDuration }).toUnixInteger() && prevSeparator !== 'now') {
              prevSeparator = 'now'
              if (j > 0) {
                item.separator = prevSeparator
              }
              item.now = true
            }
            if (ts >= DateTime.now().plus({ hours: $this.nowDuration }).toUnixInteger() && prevSeparator !== 'normal') {
              prevSeparator = 'normal'
              if (j > 0) {
                item.separator = prevSeparator
              }
            }
          }
          this.workflowItems[columnIds[i]] = args[i].data
        }
      }))
    },
    searchItems: debounce(async function (text, done) {
      this.loadColumns()
    }, 250),
  },
  watch: {
    search (newValue) {
      this.searchItems(newValue)
    },
    start (newValue) {
      this.searchItems(newValue)
    },
    end (newValue) {
      this.searchItems(newValue)
    },
  },
  components: {
    DeliveryDetails,
    WorkflowOrderDetails,
    WorkflowItem,
    ColumnSummary
  },
  mounted () {
    if (this.defaultDuration && this.defaultDuration !== 'all') {
      this.duration = this.defaultDuration
    } else {
      this.duration = ''
    }
    if (this.isDateFilterVisible) {
      this.setToday()
    } else {
      this.dateStart = null
      this.duration = null
    }
    if(!this.events) {
      let events = {}
      if (this.mode === 'orders') {
        events['orderUpdate'] = (message) => {
          if (this.shopId == message.shopId) {
            this.loadColumns()
            return
          }
        }
      }
      if (this.mode === 'deliveries') {
        events['deliveryUpdate'] = (message) => {
          if (this.shopId == message.shopId) {
            this.loadColumns()
            return
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
    this.loadTags()
    this.loadColumns()
  }
}

</script>
